// Zip Code Lookup Service
// Uses backend API to get city information from zip codes via pgeocode

import api from '@/lib/api';

/**
 * Look up city information from a zip code
 * @param {string} zipCode - 5-digit US zip code
 * @returns {Promise<{city: string, state: string, county: string, is_la_county: boolean}>}
 */
export async function lookupZipCode(zipCode) {
  if (!zipCode || zipCode.length !== 5) {
    return null;
  }
  
  try {
    const response = await api.get(`/zipcode/lookup/${zipCode}`);
    return response.data;
  } catch (error) {
    console.error('Zip code lookup error:', error);
    return null;
  }
}

/**
 * Validate a zip code and get routing information
 * @param {string} zipCode - 5-digit US zip code
 * @returns {Promise<{valid: boolean, city: string, route: 'signup'|'waitlist', message: string}>}
 */
export async function validateZipCode(zipCode) {
  if (!zipCode || zipCode.length !== 5) {
    return { valid: false, error: 'Invalid zip code format' };
  }
  
  try {
    const response = await api.get(`/zipcode/validate/${zipCode}`);
    return response.data;
  } catch (error) {
    if (error.response?.data?.detail) {
      return { valid: false, error: error.response.data.detail };
    }
    return { valid: false, error: 'Failed to validate zip code' };
  }
}

/**
 * Check if a zip code is in LA County (client-side check for quick validation)
 * This is a fallback - prefer using the backend validation for accuracy
 */
export function isLACountyZipQuick(zipCode) {
  if (!zipCode || !/^\d{5}$/.test(zipCode)) return false;
  
  const zip = parseInt(zipCode, 10);
  
  const LA_COUNTY_RANGES = [
    [90001, 90099], [90101, 90199], [90201, 90298], [90301, 90312],
    [90401, 90411], [90501, 90509], [90601, 90680], [90701, 90749],
    [90801, 90899], [91001, 91077], [91101, 91199], [91201, 91210],
    [91301, 91399], [91401, 91499], [91501, 91526], [91601, 91618],
    [91701, 91799], [91801, 91899]
  ];
  
  const LA_OUTLIERS = [
    '92397', '93243', '93510', '93532', '93534', '93535', '93536', '93539',
    '93543', '93544', '93550', '93551', '93552', '93553', '93560', '93563',
    '93584', '93586', '93590', '93591'
  ];
  
  for (const [min, max] of LA_COUNTY_RANGES) {
    if (zip >= min && zip <= max) return true;
  }
  
  return LA_OUTLIERS.includes(zipCode);
}
